import { useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import { selectFilter } from '../../../../../../store/selectors/filters'
import {
  Button,
  Link,
  Box,
  Modal,
  Text,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ButtonGroup,
  Heading,
  ModalBody,
  Flex,
  Icon,
  Form,
  FormControl,
  FormLabel,
  Input,
  useToast,
  FormErrorMessage
} from '@homebotapp/hb-react-component-catalog'
import { Link as ReactRouterLink } from 'react-router-dom'
import { useTrackingContext } from '../../../../../../providers/tracking'
import * as Sentry from '@sentry/browser'
import { selectRouteToSavedSearches } from '../../../../../../store/selectors/routes'
import {
  HOME_SEARCH_CREATE_SAVED_SEARCH_BUTTON,
  HOME_SEARCH_DISMISS_CREATE_SAVED_SEARCH_MODAL
} from '../../../../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { ReactNode } from 'react'
import { useCreateClientEvent } from '../../../../../../hooks/useCreateClientEvent'
import {
  ClientEventName,
  ListingsFilters,
  ListingsSortOrder,
  SavedSearchInput,
  SearchArea
} from '../../../../../../api/gqlaxy/gql/generated/graphql'
import { useFilters } from '../../../hook/useFilters'
import { useRecoilState } from 'recoil'
import { currentSearchLocation } from '../../../../GoogleAutoComplete/state/currentSearchLocation'
import { useCreateSavedSearch } from '../../../../SavedSearch/useCreateSavedSearch'
import { SavedSearchChangedFiltersDisplay } from '../../../../SavedSearch/SavedSearchChangedFiltersDisplay'
import { SavedSearchAlertCadenceRadioButtons } from '../../../../SavedSearch/SavedSearchAlertCadenceRadioButtons'

interface FilterTextProps {
  children: ReactNode
}
export const FilterText = ({ children }: FilterTextProps) => <Text size='sm'>{children}</Text>

export const MSG = defineMessages({
  header: {
    id: 'HomeSearch.listings.savedSearch.modal.header',
    defaultMessage: 'Save your search?'
  },
  here: {
    id: 'HomeSearch.listings.savedSearch.modal.here',
    defaultMessage: 'here'
  },
  primaryBtn: {
    id: 'HomeSearch.listings.savedSearch.modal.primaryBtn',
    defaultMessage: 'Save search'
  },
  secondaryBtn: {
    id: 'HomeSearch.listings.searchSaved.modal.cancel',
    defaultMessage: 'Cancel'
  },
  searchSaved: {
    id: 'HomeSearch.listings.savedSearch.modal.searchSaved',
    defaultMessage: 'Saved!'
  },
  youCanManage: {
    id: 'HomeSearch.listings.savedSearch.modal.youCanManage',
    defaultMessage: 'You can manage your saved searches {link}'
  },
  tooManySavedSearches: {
    id: 'HomeSearch.listings.savedSearch.modal.tooManySavedSearches',
    defaultMessage: 'You have reached the maximum number of saved searches. You can remove a saved search {link}'
  },
  nameYourSearch: {
    id: 'HomeSearch.listings.savedSearch.modal.nameYourSearch',
    defaultMessage: 'Name your search'
  },
  required: {
    id: 'HomeSearch.listings.savedSearch.modal.required',
    defaultMessage: 'required'
  },
  howOften: {
    id: 'HomeSearch.listings.savedSearch.modal.howOften',
    defaultMessage: 'How often do you want alerts?'
  },
  weWillLetYouKnow: {
    id: 'HomeSearch.listings.savedSearch.modal.weWillLetYouKnow',
    defaultMessage: "We'll let you know when new homes meet your criteria."
  },
  customArea: {
    id: 'HomeSearch.listings.savedSearch.modal.customArea',
    defaultMessage: 'Custom Area'
  },
  searchNameRequired: {
    id: 'HomeSearch.listings.savedSearch.modal.searchNameRequired',
    defaultMessage: 'Search name is required'
  }
})

export type SavedSearchAlertCadence = 'daily' | 'weekly' | 'never'

export interface ConfirmSavedSearchModalProps {
  showModal: boolean
  hideModal: () => void
}

export interface CreateSavedSearchPayload {
  client_saved_home_search: CreateSavedSearchPayloadData
}

interface CreateSavedSearchPayloadData {
  notification_intensity: string
  location_id: string
  state_abbreviation: string
  location_level: string
  location_name: string
  max_price: number | null
  max_sqft: number | null
  max_year_built: number | null
  min_bathrooms_count: number | null
  min_bedrooms_count: number | null
  min_price: number | null
  min_sqft: number | null
  min_year_built: number | null
  property_types: string[]
  statuses: string[]
}

export const getAlertCadenceDays = (alertCadence: SavedSearchAlertCadence) => {
  switch (alertCadence) {
    case 'daily':
      return 1
    case 'weekly':
      return 7
    case 'never':
      return null
    default:
      return null
  }
}

export const ConfirmSavedSearchModal = ({ hideModal, showModal }: ConfirmSavedSearchModalProps) => {
  const { trackingClient } = useTrackingContext()
  const intl = useIntl()
  const toast = useToast()
  const [searchLocation] = useRecoilState(currentSearchLocation)
  const filter = useSelector(selectFilter)
  const { filters } = useFilters()
  const { mutateAsync: createSavedSearch, isLoading: isCreatingSavedSearch } = useCreateSavedSearch()

  const savedSearchesRoute = useSelector(selectRouteToSavedSearches)

  const { mutateAsync: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })

  interface CreateSavedSearchFormData {
    searchName: string
    alertCadence: SavedSearchAlertCadence
  }
  const handleSubmit = async (data: CreateSavedSearchFormData) => {
    const payload: SavedSearchInput = {
      name: data.searchName,
      alertCadenceDays: getAlertCadenceDays(data.alertCadence),
      filters: filters?.filters as ListingsFilters,
      searchArea: filters?.searchArea as SearchArea,
      sortOrder: filters?.sortOrder as ListingsSortOrder
    }
    try {
      await createSavedSearch({ input: payload })
      trackingClient?.searchSaved()
      createClientEvent({
        clientEventInput: {
          eventName: ClientEventName.SavedSearchCreatedEvent
        }
      })
      toast({
        status: 'success',
        title: intl.formatMessage(MSG.searchSaved),
        description: intl.formatMessage(MSG.youCanManage, {
          link: <Link href={savedSearchesRoute}>{intl.formatMessage(MSG.here)}</Link>
        })
      })
    } catch (error: any) {
      if (error.status === 422) {
        toast({
          status: 'error',
          description: intl.formatMessage(MSG.tooManySavedSearches, {
            link: (
              <Link as={ReactRouterLink} to={savedSearchesRoute}>
                {intl.formatMessage(MSG.here)}
              </Link>
            )
          })
        })
      } else {
        Sentry.captureException(error)
      }
    }

    hideModal()
  }

  return (
    <Modal isOpen={showModal} onClose={hideModal}>
      <Form<CreateSavedSearchFormData>
        defaultValues={{ alertCadence: 'daily', searchName: '' }}
        onSubmit={data => handleSubmit(data)}
      >
        {({ formState: { errors } }) => (
          <>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Heading textAlign='left' as='h2' mb={0}>
                  {intl.formatMessage(MSG.header)}
                </Heading>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Box>
                  <Box
                    bgColor='card-bg-layer-3'
                    paddingX={5}
                    paddingY={3}
                    border='1px solid var(--brand-primary-border-50)'
                    borderRadius={5}
                    mb={5}
                  >
                    <Flex alignItems='center' mb={1}>
                      <Icon width='16px' height='16px' name='map-marker-mdc' color='neutral.900' />
                      <Text fontWeight='semibold' mb={0} ml={2} color='neutral.900'>
                        {filters?.searchArea.boundingBox
                          ? intl.formatMessage(MSG.customArea)
                          : `${searchLocation?.terms?.[0]?.value}, ${searchLocation?.terms?.[1]?.value}`}
                      </Text>
                    </Flex>
                    <SavedSearchChangedFiltersDisplay savedSearchOrFilter={filters?.filters} />
                  </Box>
                  <FormControl isInvalid={!!errors?.searchName}>
                    <RequiredFormLabel size='sm'>{intl.formatMessage(MSG.nameYourSearch)}</RequiredFormLabel>
                    <Input
                      name='searchName'
                      size='sm'
                      mb={5}
                      rules={{
                        required: {
                          value: true,
                          message: intl.formatMessage(MSG.searchNameRequired)
                        }
                      }}
                    />
                    <FormErrorMessage>{errors?.searchName?.message?.toString()}</FormErrorMessage>

                    <RequiredFormLabel size='sm'>{intl.formatMessage(MSG.howOften)}</RequiredFormLabel>
                    <SavedSearchAlertCadenceRadioButtons
                      tracking={{
                        guid: 'pJMxDMwnn6WoAMETnDaPUR',
                        ui_context: 'Create Saved Search Modal',
                        descriptor: 'Alert frequency radio button group'
                      }}
                    />
                  </FormControl>
                  <Text size='sm' mb={4}>
                    {intl.formatMessage(MSG.weWillLetYouKnow)}
                  </Text>
                </Box>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup spacing={3}>
                  <Button
                    tracking={{
                      guid: '3NEqgnRrz6LPZWcAobr5NL',
                      ui_context: HOME_SEARCH_DISMISS_CREATE_SAVED_SEARCH_MODAL,
                      descriptor: 'Dismiss saved search modal'
                    }}
                    onClick={hideModal}
                  >
                    {intl.formatMessage(MSG.secondaryBtn)}
                  </Button>
                  <Button
                    type='submit'
                    isDisabled={isCreatingSavedSearch}
                    tracking={{
                      guid: 'tXDMYU9nsgMdKaT6rjGzNW',
                      ui_context: HOME_SEARCH_CREATE_SAVED_SEARCH_BUTTON,
                      descriptor: 'Create saved search'
                    }}
                    colorScheme='primary'
                  >
                    {intl.formatMessage(MSG.primaryBtn)}
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </>
        )}
      </Form>
    </Modal>
  )
}

interface RequiredFormLabelProps {
  children: ReactNode
  size: 'sm' | 'md' | 'lg'
}

export const RequiredFormLabel = ({ children, size }: RequiredFormLabelProps) => {
  const intl = useIntl()
  return (
    <Flex>
      <FormLabel size={size} marginRight={1}>
        {children}
        <Text srOnly as='span'>
          {intl.formatMessage(MSG.required)}
        </Text>
      </FormLabel>
      <Text as='span' color='danger' aria-hidden='true'>
        *
      </Text>
    </Flex>
  )
}
